import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge } from '@/components/StatusBadge';
import { Pagination } from '@/components/Pagination';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Search, Eye } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import api from '@/lib/api';

const PAGE_SIZE = 10;

export default function ApplicationList() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [apps, setApps] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    api.get('/applications').then(r => setApps(r.data)).catch(() => {});
  }, []);

  const filtered = apps.filter(a => {
    if (filter !== 'all' && a.status !== filter) return false;
    if (search) {
      const name = (a.personal_info?.full_name || '').toLowerCase();
      return name.includes(search.toLowerCase());
    }
    return true;
  });

  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => { setPage(1); }, [filter, search]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="application-list-page">
      <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('embassy.applications')}</h1>

      <Card className="border">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder={t('emb.search')} className="pl-10" value={search} onChange={e => setSearch(e.target.value)} data-testid="search-input" />
            </div>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-40" data-testid="status-filter">
                <SelectValue placeholder={t('emb.filter')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('emb.all')}</SelectItem>
                <SelectItem value="submitted">{t('status.submitted')}</SelectItem>
                <SelectItem value="approved">{t('status.approved')}</SelectItem>
                <SelectItem value="rejected">{t('status.rejected')}</SelectItem>
                <SelectItem value="correction_needed">{t('status.correction_needed')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('app.full_name')}</TableHead>
                  <TableHead>{t('app.country')}</TableHead>
                  <TableHead>{t('card.status')}</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginated.map(app => (
                  <TableRow key={app.application_id}>
                    <TableCell className="font-medium">{app.personal_info?.full_name || '-'}</TableCell>
                    <TableCell>{app.personal_info?.country_of_residence || '-'}</TableCell>
                    <TableCell><StatusBadge status={app.status} /></TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {app.created_at ? new Date(app.created_at).toLocaleDateString('fr-FR') : '-'}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/embassy/applications/${app.application_id}`)} data-testid={`view-app-${app.application_id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {paginated.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">{t('emb.no_app')}</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <Pagination total={filtered.length} pageSize={PAGE_SIZE} currentPage={page} onPageChange={setPage} />
        </CardContent>
      </Card>
    </div>
  );
}
